/** @jsxRuntime classic */
/** @jsx jsx */
import { Code, ConnectError } from "@bufbuild/connect";
import { css, jsx } from "@emotion/react";
import { SPView, PCView } from "components/common/MediaQuery";
import React from "react";
import { Redirect } from "react-router-dom";

import { useFetchAdminTicket } from "./useFetchAdminTicket";

export const AdminTicketDashboard: React.FC = () => {
  const {
    displayTicketSummary,
    overallSummary,
    isLoading,
    isValidating,
    error,
    lastUpdated,
    mutate,
  } = useFetchAdminTicket();

  const handleRefresh = async () => {
    try {
      await mutate();
    } catch (error) {
      console.error("Failed to refresh:", error);
      alert("データの更新に失敗しました。再度お試しください。");
    }
  };

  if (error) {
    if (error instanceof ConnectError) {
      if (
        error.code === Code.PermissionDenied ||
        error.code === Code.Unauthenticated ||
        error.code === Code.InvalidArgument
      ) {
        return <Redirect to="/" />;
      }
    }
    return (
      <div css={styles.container}>
        <p style={{ textAlign: "center", marginTop: "40px", color: "#ff4d4f" }}>
          {error.message ?? "データの取得に失敗しました"}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div css={styles.container}>
        <p
          style={{ textAlign: "center", marginTop: "40px", color: "#ffffffff" }}
        >
          読み込み中...
        </p>
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <div css={styles.headerText}>チケット状況</div>
      <div css={styles.lastUpdatedContainer}>
        <span>
          データ取得日時:{lastUpdated ? lastUpdated.toLocaleString() : "未取得"}
        </span>
        <button
          onClick={handleRefresh}
          css={styles.refreshButton}
          disabled={isLoading || isValidating}
        >
          {isLoading || isValidating ? "更新中..." : "更新"}
        </button>
      </div>
      <PCView>
        <h2 css={styles.sectionHeader}>全体集計</h2>
        <div css={styles.tableWrapper}>
          <table css={styles.table}>
            <thead>
              <tr css={styles.headerRow}>
                <th>購入チケット総数</th>
                <th>チケットもぎり総数</th>
                <th>チケットもぎり全体率</th>
                <th>特典つきチケット総数</th>
                <th>特典もぎり総数</th>
                <th>特典もぎり全体率</th>
              </tr>
            </thead>
            <tbody>
              <tr css={styles.rowItem}>
                <td css={styles.rowText}>{overallSummary.purchasedCount}</td>
                <td css={styles.rowText}>{overallSummary.checkedInCount}</td>
                <td css={styles.rowText}>{overallSummary.checkedInRate}%</td>
                <td css={styles.rowText}>{overallSummary.redeemableCount}</td>
                <td css={styles.rowText}>{overallSummary.redeemedCount}</td>
                <td css={styles.rowText}>{overallSummary.redeemedRate}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h2 css={styles.sectionHeader}>
          チケット別 ({displayTicketSummary?.length ?? 0}件)
        </h2>
        <div css={styles.tableWrapper}>
          <table css={styles.table}>
            <thead>
              <tr css={styles.headerRow}>
                <th>チケットID</th>
                <th>チケット名</th>
                <th>購入数</th>
                <th>チケットもぎり数</th>
                <th>チケットもぎり率</th>
                <th>特典もぎり数</th>
                <th>特典もぎり率</th>
              </tr>
            </thead>

            <tbody>
              {displayTicketSummary?.map((t) => (
                <tr key={t.ticketId} css={styles.rowItem}>
                  <td css={styles.rowText}>{t.ticketId}</td>
                  <td style={{ fontWeight: "bold" }}>{t.ticketName}</td>
                  <td>{t.purchasedCount}</td>
                  <td css={styles.rowText}>{t.checkedInCount}</td>
                  <td css={styles.rowText}>{t.checkedInRate}%</td>
                  <td>{t.redeemedCount}</td>
                  <td css={styles.rowText}>{t.redeemedRate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {displayTicketSummary?.length === 0 && (
          <p style={{ textAlign: "center", marginTop: "40px", color: "#666" }}>
            データが見つかりません
          </p>
        )}
      </PCView>
      <SPView>
        <h2 css={styles.sectionHeader}>全体集計</h2>
        <div css={styles.spCardList}>
          <div css={styles.spCard}>
            <div css={styles.spDetailGrid}>
              <div css={styles.spDetailItem}>
                <div css={styles.spLabel}>チケットもぎり</div>
                <div css={styles.spValue}>
                  {overallSummary.checkedInRate}%
                  <span css={styles.spSubUnit}>
                    ({overallSummary.checkedInCount} /{" "}
                    {overallSummary.purchasedCount})
                  </span>
                </div>
                <div css={styles.progressBarBg}>
                  <div css={styles.progressBar(overallSummary.checkedInRate)} />
                </div>
              </div>
              <div css={styles.spDetailItem}>
                <div css={styles.spLabel}>特典もぎり</div>
                <div css={styles.spValue}>
                  {overallSummary.redeemedRate}%
                  <span css={styles.spSubUnit}>
                    ({overallSummary.redeemedCount} /{" "}
                    {overallSummary.redeemableCount})
                  </span>
                </div>
                <div css={styles.progressBarBg}>
                  <div css={styles.progressBar(overallSummary.redeemedRate)} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <h2 css={styles.sectionHeader}>
          チケット別 ({displayTicketSummary?.length ?? 0}件)
        </h2>
        <div css={styles.spCardList}>
          {displayTicketSummary && displayTicketSummary.length === 0 && (
            <p
              style={{ textAlign: "center", marginTop: "40px", color: "#666" }}
            >
              データが見つかりません
            </p>
          )}
          {displayTicketSummary?.map((t) => (
            <div key={t.ticketId} css={styles.spCard}>
              <div css={styles.spCardHeader}>
                <span css={styles.spTicketId}>{t.ticketId}</span>
                <div css={styles.spTicketName}>{t.ticketName}</div>
              </div>

              <div css={styles.spDetailGrid}>
                {/* チケットもぎりセクション */}
                <div css={styles.spDetailItem}>
                  <div css={styles.spLabel}>チケットもぎり</div>
                  <div css={styles.spValue}>
                    {t.checkedInRate}%
                    <span css={styles.spSubUnit}>
                      ({t.checkedInCount} / {t.purchasedCount})
                    </span>
                  </div>
                  <div css={styles.progressBarBg}>
                    <div css={styles.progressBar(t.checkedInRate)} />
                  </div>
                </div>

                {/* 特典もぎりセクション */}
                <div css={styles.spDetailItem}>
                  <div css={styles.spLabel}>特典もぎり</div>
                  <div css={styles.spValue}>
                    {t.redeemedRate}%
                    <span css={styles.spSubUnit}>
                      ({t.redeemedCount} / {t.redeemableCount})
                    </span>
                  </div>
                  <div css={styles.progressBarBg}>
                    <div css={styles.progressBar(t.redeemedRate)} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SPView>
    </div>
  );
};
//TODO: 仮のデザイン
const styles = {
  container: css({
    backgroundColor: "#000000",
    color: "#ffffff",
    padding: "24px",
    minHeight: "100vh",
    fontFamily: "sans-serif",
  }),
  headerText: css({
    color: "#ffffff",
    fontSize: "1.5rem",
    fontWeight: "bold",
    marginBottom: "20px",
  }),
  sectionHeader: css({
    color: "#ffffff",
    fontSize: "1.15rem",
    fontWeight: "bold",
    marginTop: "32px",
    marginBottom: "12px",
    paddingBottom: "8px",
    borderBottom: "1px solid #333",
  }),
  lastUpdatedContainer: css({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "0.85rem",
    color: "#ffffff",
    marginBottom: "20px",
  }),
  refreshButton: css({
    padding: "6px 12px",
    backgroundColor: "#1f1f1f",
    color: "#ffffff",
    border: "1px solid #aaaaaa",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "0.8rem",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#333333",
    },
    "&:active": {
      backgroundColor: "#000000",
    },
  }),
  tableWrapper: css({
    width: "100%",
    marginTop: "20px",
    backgroundColor: "#111111",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid #333",
  }),
  table: css({
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  }),
  headerRow: css({
    backgroundColor: "#1f1f1f",
    borderBottom: "2px solid #333",
    "& th": {
      padding: "12px 16px",
      fontSize: "0.85rem",
      color: "#9ca3af",
      fontWeight: "bold",
      textAlign: "left",
    },
    // idと名前の列を広くする
    "& th:first-of-type": { width: "2fr" },
    "& th:nth-of-type(2)": { width: "2fr" },
  }),
  rowItem: css({
    borderBottom: "1px solid #222",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#1a1a1a",
    },
    "& td": {
      padding: "16px",
      fontSize: "0.95rem",
      verticalAlign: "middle",
    },
  }),
  rowText: css({
    color: "#fff",
    fontSize: "0.9rem",
  }),
  spCardList: css({
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  }),
  spCard: css({
    backgroundColor: "#111111",
    borderRadius: "12px",
    padding: "16px",
    border: "1px solid #333",
  }),
  spCardHeader: css({
    borderBottom: "1px solid #222",
    paddingBottom: "12px",
    marginBottom: "12px",
  }),
  spTicketId: css({
    fontSize: "0.75rem",
    color: "#ffffffff",
    display: "block",
    marginBottom: "4px",
  }),
  spTicketName: css({
    fontSize: "1.1rem",
    fontWeight: "bold",
    color: "#fff",
  }),
  spLabel: css({
    fontSize: "1.0rem",
    color: "#ffffffff",
    marginBottom: "4px",
  }),
  spDetailGrid: css({
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  }),
  spDetailItem: css({
    backgroundColor: "#1a1a1a",
    padding: "10px",
    borderRadius: "8px",
  }),
  spValue: css({
    fontSize: "1rem",
    fontWeight: "bold",
  }),
  spSubUnit: css({
    fontSize: "0.85rem",
    color: "#a0a0a0",
    marginLeft: "8px",
  }),
  progressBarBg: css({
    width: "100%",
    height: "4px",
    backgroundColor: "#333",
    borderRadius: "2px",
    marginTop: "8px",
    overflow: "hidden",
  }),
  progressBar: (rate: number) => {
    const clampedRate = Math.max(0, Math.min(rate, 100)); // 0%未満と100%を超える値をクランプ
    return css({
      width: `${clampedRate}%`,
      height: "100%",
      backgroundColor: rate >= 100 ? "#52c41a" : "#3b82f6",
      transition: "width 0.3s ease-in-out",
    });
  },
};
